import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@features/auth/services/auth.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { catchError, Observable, Subject, switchMap, take, throwError } from 'rxjs';

/**
 * Decodifica el JWT y comprueba si ha expirado.
 * Devuelve `true` si el token expiró o no es válido.
 */
function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

let isRefreshing = false;

/**
 * Subject que emite el nuevo accessToken cuando el refresh completa con éxito,
 * o `null` cuando falla. Las requests encoladas durante el refresh escuchan aquí.
 */
const refreshToken$ = new Subject<string | null>();

/** Evita navegaciones duplicadas cuando varias requests reciben 503 a la vez. */
let isNavigatingToServiceUnavailable = false;

/**
 * Interceptor que maneja errores 401 distinguiendo dos casos:
 * - Token expirado → intenta refrescar; si falla, cierra sesión.
 * - Sin permisos (token válido) → muestra mensaje al usuario, no intenta refresh.
 *
 * Las requests simultáneas que lleguen con 401 mientras ya se está refrescando
 * quedan encoladas y se reintentarán con el nuevo token al completarse el refresh.
 *
 * Un 503 (servicio en despliegue/mantenimiento) navega a una pantalla dedicada en
 * vez de propagar el error, para evitar que cada petición fallida muestre su
 * propio mensaje de error mientras la API no está disponible.
 */
export function HttpErrorsInterceptor(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> {
  const authService = inject(AuthService);
  const snackbarService = inject(SnackbarService);
  const router = inject(Router);

  if (req.url.includes('RefreshToken')) return next(req);

  const isAccountLoginRequest = req.url.includes('/api/account/login');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 503) {
        if (!isNavigatingToServiceUnavailable) {
          isNavigatingToServiceUnavailable = true;
          const currentUrl = router.url;
          const redirectTo = currentUrl.startsWith('/service-unavailable')
            ? undefined
            : currentUrl;
          void router
            .navigate(['/service-unavailable'], {
              queryParams: redirectTo ? { redirectTo } : undefined,
            })
            .finally(() => (isNavigatingToServiceUnavailable = false));
        }
        // Se propaga el error (en vez de EMPTY) para que cada `rxResource`/`finalize`
        // que esté esperando esta petición pueda salir de su estado de carga; para
        // entonces la navegación ya sacó al usuario de la vista que la disparó.
        return throwError(() => error);
      }

      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (isAccountLoginRequest) {
        return throwError(() => error);
      }

      // Token aún vigente → el 401 es por falta de permisos, no por expiración
      if (!isTokenExpired(authService.token)) {
        snackbarService.open('No cuenta con permisos para acceder a este recurso.', 'Cerrar', {
          panelClass: 'snackbar-warning',
        });
        return throwError(() => error);
      }

      // Token expirado y ya hay un refresh en curso → encolar esta request.
      // Si el refresh en curso falla (`refreshToken$.next(null)`), propagar el
      // error original en vez de descartarlo: de lo contrario esta request
      // queda esperando para siempre (nunca emite next/error/complete).
      if (isRefreshing) {
        return refreshToken$.pipe(
          take(1),
          switchMap((token) =>
            token
              ? next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
              : throwError(() => error),
          ),
        );
      }

      // Token expirado, iniciar refresh
      isRefreshing = true;

      return authService.refreshToken().pipe(
        switchMap(({ accessToken }) => {
          isRefreshing = false;
          refreshToken$.next(accessToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }));
        }),
        catchError((refreshError) => {
          isRefreshing = false;
          refreshToken$.next(null);
          authService.signout();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
}
